import React, { useState, useRef } from 'react';

import { Controller, type FieldErrors, useForm } from 'react-hook-form';
import { ActionBar } from '../../shared-components/ActionBar/ActionBar';
import { CtaButton } from '../../shared-components/CtaButton/CtaButton';
import { Input } from '../../shared-components/Input/Input';
import { PageHeader } from '../../shared-components/PageHeader/PageHeader';
import { Link } from '../../shared-components/Link/Link';

import TCClient from '../../../client';

import useClickOutside from '../../../common/hooks/useClickOutside';
import styles from './AddUser.module.css';
import { PasswordToolTip } from './PasswordToolTip';
import { actions, useAppDispatch, useAppSelector } from 'redux-stores';

const { setUsersPageView } = actions;

type FieldKey =
  | 'firstName'
  | 'lastName'
  | 'displayName'
  | 'emailAddress'
  | 'password'
  | 'title'
  | 'department';

type Field = {
  label: string;
  fieldKey: FieldKey;
  isRequired: boolean;
  errorMessage?: string;
  regex?: RegExp;
  regexErrorMessage?: string;
};

const FIELD_KEYS: Field[] = [
  {
    label: 'First Name',
    fieldKey: 'firstName',
    isRequired: true,
    errorMessage: 'First Name is required.',
  },
  {
    label: 'Last Name',
    fieldKey: 'lastName',
    isRequired: true,
    errorMessage: 'Last Name is required.',
  },
  {
    label: 'Display Name',
    fieldKey: 'displayName',
    isRequired: true,
    errorMessage: 'Display Name is required.',
  },
  {
    label: 'Email Address',
    fieldKey: 'emailAddress',
    isRequired: true,
    errorMessage: 'Email Address is required.',
  },
  {
    label: 'Password',
    fieldKey: 'password',
    isRequired: false,
    regex: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/,
    regexErrorMessage:
      'Password must contain a lowercase letter, an uppercase letter, a number, a special character, and be at least 8 characters long.',
  },
  {
    label: 'Title',
    fieldKey: 'title',
    isRequired: false,
  },
  {
    label: 'Department',
    fieldKey: 'department',
    isRequired: false,
  },
];

type FormFields = { [key in FieldKey]: string };

export default function AddUser() {
  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    reset,
  } = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      displayName: '',
      emailAddress: '',
      password: '',
      title: '',
      department: '',
    },
  });
  const passwordValue = watch('password');
  const selectedOrganization = useAppSelector((state) => state.admin.selectedOrganization);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPasswordToolTip, setShowPasswordToolTip] = useState(false);
  const passwordInput = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();

  const focusFirstErrorField = (formErrors: FieldErrors<FormFields>) => {
    const firstErrorField = Object.keys(formErrors).reduce(
      (field: string | null, currentField: string) => {
        return field || (errors[currentField as keyof FormFields] ? currentField : null);
      },
      null
    );

    if (firstErrorField) {
      const input = document.getElementById(firstErrorField) as HTMLInputElement;
      if (input) {
        input.focus();
      }
    }
  };

  async function onSubmit(data: FormFields) {
    try {
      setIsSubmitting(true);
      await TCClient.adminUsers.createUser({
        ...data,
        organizationId: selectedOrganization.token,
      });
      reset();
      alert('User was successfully created.');
    } catch (e) {
      alert('Error when creating user.');
      console.error(e);
    } finally {
      setIsSubmitting(false);
    }
  }

  useClickOutside([passwordInput], () => {
    setShowPasswordToolTip(false);
  });

  return (
    <form
      className={styles.root}
      onSubmit={handleSubmit((data) => onSubmit(data as FormFields), focusFirstErrorField)}
    >
      <PageHeader
        label={'Users'}
        subLabel={'New User'}
        onClick={() => {
          dispatch(setUsersPageView({ selectedUsersPageView: 'users' }));
        }}
      />
      <div className={styles.headerSeparator} />
      <ActionBar tabButtons={[{ label: 'User Settings' }]} />
      <div>
        {FIELD_KEYS.map(
          ({ label, fieldKey, isRequired, errorMessage, regex, regexErrorMessage }) => (
            <div className={styles.field} key={fieldKey}>
              <label htmlFor={fieldKey} className={styles.label}>
                {label}
                {isRequired ? '*' : ''}
              </label>
              <div className={styles.inputContainer}>
                <Controller
                  name={fieldKey}
                  control={control}
                  rules={{
                    required: isRequired ? errorMessage : false,
                    pattern: regex
                      ? {
                          value: regex,
                          message: regexErrorMessage ?? '',
                        }
                      : undefined,
                  }}
                  render={({ field: { onChange, value } }) => (
                    <Input
                      id={fieldKey}
                      customCSS={`${styles.input} ${errors[fieldKey] ? styles.inputError : ''}`}
                      value={value}
                      inputRef={label === 'Password' ? passwordInput : undefined}
                      onFocus={() => {
                        if (label === 'Password') {
                          setShowPasswordToolTip(true);
                        } else {
                          setShowPasswordToolTip(false);
                        }
                      }}
                      onInputChange={(e) => {
                        onChange(e.target.value);
                      }}
                    />
                  )}
                />
                {errors[fieldKey] && (
                  <span className={styles.errorMessage}>{errors[fieldKey]?.message as string}</span>
                )}
              </div>
              {label === 'Password' && showPasswordToolTip && (
                <div className={styles.passwordToolTipContainer}>
                  <PasswordToolTip key={`password-tooltip`} value={passwordValue} />
                </div>
              )}
            </div>
          )
        )}
      </div>
      <div className={styles.buttonContainer}>
        <CtaButton
          label={'Create User'}
          primary
          size={'large'}
          type="submit"
          disabled={isSubmitting}
        />
        <div className={styles.canceContainer}>
          <Link
            onClick={() => {
              dispatch(setUsersPageView({ selectedUsersPageView: 'users' }));
            }}
          >
            Cancel
          </Link>
        </div>
      </div>
    </form>
  );
}
